import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { motion } from 'framer-motion'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Users, Edit, Trash2, Shield, User, UserPlus, Search, Loader2, Building2, Briefcase } from 'lucide-react'
import { api } from '@/lib/api-client'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/use-toast'
import { User as UserType } from '@/features/auth/types'
import type { Role } from '@/features/roles/api'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'
import { buildPagedParams } from '@/shared/types'

interface DirectoryUser {
    azure_ad_id: string
    name: string
    email: string
    job_title?: string
    department?: string
}

export function SettingsUsersPage() {
    const [editingUser, setEditingUser] = useState<UserType | null>(null)
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [userToDelete, setUserToDelete] = useState<UserType | null>(null)
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
    const [directorySearch, setDirectorySearch] = useState('')
    const [selectedRole, setSelectedRole] = useState('user')
    const [editRoles, setEditRoles] = useState<string[]>([])
    const { toast } = useToast()
    const queryClient = useQueryClient()

    const listState = useListState({ defaultPerPage: 10, defaultSortBy: 'created_at' })

    // ── Queries ──────────────────────────────────────────────────────────
    const { data: usersResponse, isLoading, error } = useQuery({
        queryKey: ['admin', 'users', listState.queryParams],
        queryFn: () => api.users.list(Object.fromEntries(buildPagedParams(listState.queryParams))),
        placeholderData: keepPreviousData,
    })

    const usersData = usersResponse?.data ?? usersResponse
    const users: UserType[] = usersData?.items ?? []
    const totalItems = usersData?.total ?? 0
    const totalPages = usersData?.pages ?? 1

    const { data: rolesResponse } = useQuery({
        queryKey: ['roles'],
        queryFn: () => api.roles.list(),
    })

    const allRoles: Role[] = rolesResponse?.data?.roles || []

    const { data: directoryResponse, isLoading: isSearching, refetch: searchDirectory } = useQuery({
        queryKey: ['directory-users', directorySearch],
        queryFn: () => api.users.searchDirectory(directorySearch),
        enabled: isAddDialogOpen,
        staleTime: 0,
    })

    const directoryUsers: DirectoryUser[] = directoryResponse?.data?.users || []

    // ── Mutations ────────────────────────────────────────────────────────
    const updateUserMutation = useMutation({
        mutationFn: ({ userId, userData }: { userId: string; userData: any }) =>
            api.users.update(userId, userData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            setIsEditDialogOpen(false)
            setEditingUser(null)
            toast({ title: 'User updated', description: 'User information has been updated successfully.' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.detail || error.response?.data?.error || 'Failed to update user',
                variant: 'destructive',
            })
        }
    })

    const deleteUserMutation = useMutation({
        mutationFn: (userId: string) => api.users.delete(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
            setIsDeleteDialogOpen(false)
            setUserToDelete(null)
            toast({ title: 'User deleted', description: 'User has been deactivated successfully.' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.detail || error.response?.data?.error || 'Failed to delete user',
                variant: 'destructive',
            })
        }
    })

    const provisionMutation = useMutation({
        mutationFn: (userData: DirectoryUser & { role: string }) => api.users.provision(userData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
            queryClient.invalidateQueries({ queryKey: ['directory-users'] })
            toast({ title: 'User added', description: 'User has been provisioned successfully.' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.detail || error.response?.data?.error || 'Failed to provision user',
                variant: 'destructive',
            })
        }
    })

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleEditUser = (user: UserType) => {
        setEditingUser(user)
        setEditRoles(user.roles?.map(r => r.name) || [])
        setIsEditDialogOpen(true)
    }

    const handleDeleteUser = (user: UserType) => {
        setUserToDelete(user)
        setIsDeleteDialogOpen(true)
    }

    const handleUpdateUser = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!editingUser) return

        const formData = new FormData(e.currentTarget)
        const userData = {
            name: formData.get('name') as string,
            email: formData.get('email') as string,
            roles: editRoles,
            is_active: formData.get('is_active') === 'true'
        }

        updateUserMutation.mutate({ userId: editingUser.id, userData })
    }

    const handleDeleteConfirm = () => {
        if (userToDelete) {
            deleteUserMutation.mutate(userToDelete.id)
        }
    }

    const handleProvisionUser = (adUser: DirectoryUser) => {
        provisionMutation.mutate({ ...adUser, role: selectedRole })
    }

    const handleDirectorySearch = (e: React.FormEvent) => {
        e.preventDefault()
        searchDirectory()
    }

    const toggleEditRole = (roleName: string) => {
        setEditRoles(prev =>
            prev.includes(roleName)
                ? prev.filter(r => r !== roleName)
                : [...prev, roleName]
        )
    }

    // ── Loading / Error states ──────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Users</h2>
                    <p className="text-gray-600 mb-4">Failed to load users. Please try again.</p>
                    <p className="text-sm text-gray-500">
                        Error: {(error as any)?.message || 'Unknown error'}
                    </p>
                </div>
            </div>
        )
    }

    // ── Render ───────────────────────────────────────────────────────────
    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
        >
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            All Users
                        </CardTitle>
                        <CardDescription>
                            Manage user accounts, roles, and permissions
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <ImportExportButtons
                            entityType="users"
                            entityLabel="Users"
                        />
                        <AuthorizationGate actions={['view_all_users']}>
                            <Button onClick={() => { setIsAddDialogOpen(true); setDirectorySearch('') }}>
                                <UserPlus className="h-4 w-4 mr-2" />
                                Add from AD
                            </Button>
                        </AuthorizationGate>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Toolbar */}
                    <div className="mb-4">
                        <DataToolbar
                            listState={listState}
                            searchPlaceholder="Search users..."
                            sortOptions={[
                                { label: 'Name', value: 'name' },
                                { label: 'Email', value: 'email' },
                                { label: 'Created', value: 'created_at' },
                            ]}
                            totalItems={totalItems}
                        />
                    </div>
                    <div className="space-y-4">
                        {users.length > 0 ? users.map((user: UserType) => (
                            <motion.div
                                key={user.id}
                                className="flex items-center justify-between p-4 border rounded-lg"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                                        <span className="text-sm font-medium text-white">
                                            {user.name.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <div className="font-medium">{user.name}</div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                        <div className="flex items-center gap-2 mt-1">
                                            {user.roles && user.roles.length > 0 ? (
                                                user.roles.map(r => (
                                                    <Badge
                                                        key={r.id}
                                                        variant={r.name === 'admin' ? 'default' : 'secondary'}
                                                    >
                                                        {r.name === 'admin' ? (
                                                            <>
                                                                <Shield className="h-3 w-3 mr-1" />
                                                                Admin
                                                            </>
                                                        ) : (
                                                            <>
                                                                <User className="h-3 w-3 mr-1" />
                                                                {r.name}
                                                            </>
                                                        )}
                                                    </Badge>
                                                ))
                                            ) : (
                                                <Badge variant="outline">No roles</Badge>
                                            )}
                                            <Badge variant={user.is_active ? 'default' : 'destructive'}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <AuthorizationGate actions={['update_user']}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEditUser(user)}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                    </AuthorizationGate>
                                    <AuthorizationGate actions={['delete_user']}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleDeleteUser(user)}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </AuthorizationGate>
                                </div>
                            </motion.div>
                        )) : (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No users found.</p>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    <DataPagination
                        page={listState.page}
                        totalPages={totalPages}
                        onPageChange={listState.setPage}
                        totalItems={totalItems}
                        perPage={listState.perPage}
                    />
                </CardContent>
            </Card>

            {/* ── Edit User Dialog ──────────────────────────────────────── */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit User</DialogTitle>
                        <DialogDescription>
                            Update user information and permissions
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUpdateUser}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="name">Name</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    defaultValue={editingUser?.name}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    defaultValue={editingUser?.email}
                                    required
                                />
                            </div>
                            <div>
                                <Label>Roles</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {allRoles.map(role => (
                                        <Badge
                                            key={role.id}
                                            variant={editRoles.includes(role.name) ? 'default' : 'outline'}
                                            className="cursor-pointer select-none"
                                            onClick={() => toggleEditRole(role.name)}
                                        >
                                            {editRoles.includes(role.name) ? '✓ ' : ''}{role.name}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="is_active">Status</Label>
                                <Select name="is_active" defaultValue={editingUser?.is_active ? 'true' : 'false'}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">Active</SelectItem>
                                        <SelectItem value="false">Inactive</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={updateUserMutation.isPending}>
                                {updateUserMutation.isPending ? 'Updating...' : 'Update User'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ── Delete User Dialog ────────────────────────────────────── */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete User</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to deactivate <strong>{userToDelete?.name}</strong>?
                            They will no longer be able to access the system.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteConfirm}
                            disabled={deleteUserMutation.isPending}
                        >
                            {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Add from AD Dialog ────────────────────────────────────── */}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="h-5 w-5" />
                            Add User from Directory
                        </DialogTitle>
                        <DialogDescription>
                            Search your organization's Azure AD directory and provision users
                        </DialogDescription>
                    </DialogHeader>

                    {/* Search bar */}
                    <form onSubmit={handleDirectorySearch} className="flex gap-2">
                        <Input
                            placeholder="Search by name or email…"
                            value={directorySearch}
                            onChange={(e) => setDirectorySearch(e.target.value)}
                            className="flex-1"
                        />
                        <Button type="submit" variant="outline" size="icon" disabled={isSearching}>
                            {isSearching ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Search className="h-4 w-4" />
                            )}
                        </Button>
                    </form>

                    {/* Role selector */}
                    <div className="flex items-center gap-3">
                        <Label className="text-sm text-gray-600 whitespace-nowrap">Assign role:</Label>
                        <Select value={selectedRole} onValueChange={setSelectedRole}>
                            <SelectTrigger className="w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {allRoles.map(role => (
                                    <SelectItem key={role.id} value={role.name}>
                                        {role.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Results */}
                    <div className="max-h-80 overflow-y-auto space-y-2">
                        {isSearching ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                            </div>
                        ) : directoryUsers.length > 0 ? (
                            directoryUsers.map((adUser) => (
                                <div
                                    key={adUser.azure_ad_id}
                                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                                            <span className="text-sm font-medium">
                                                {adUser.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <div className="font-medium text-sm">{adUser.name}</div>
                                            <div className="text-xs text-gray-500">{adUser.email}</div>
                                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                                                {adUser.job_title && (
                                                    <span className="flex items-center gap-1">
                                                        <Briefcase className="h-3 w-3" />
                                                        {adUser.job_title}
                                                    </span>
                                                )}
                                                {adUser.department && (
                                                    <span className="flex items-center gap-1">
                                                        <Building2 className="h-3 w-3" />
                                                        {adUser.department}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => handleProvisionUser(adUser)}
                                        disabled={provisionMutation.isPending}
                                    >
                                        {provisionMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            'Add'
                                        )}
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-400 text-sm">
                                {directorySearch
                                    ? 'No users found matching your search'
                                    : 'Search for users in the directory'}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </motion.div>
    )
}
